import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Minus, Plus, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  summarizeSelection,
  type CollectionSelection,
  type RentalContractItem,
} from '@/lib/rentalCollectionItems';

interface CollectionItemsChecklistProps {
  items: RentalContractItem[];
  selection: CollectionSelection;
  onChange: (next: CollectionSelection) => void;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * L-LOC.9b FRONT-R (D-LOC.31/36/37): o que está sendo coletado DE FATO.
 * Marcar/desmarcar por linha; linha com mais de 1 ajusta a quantidade;
 * item por unidade (série) só sai inteiro. Usado na assinatura da coleta
 * (motorista/Logística) e na devolução de balcão.
 */
export function CollectionItemsChecklist({
  items,
  selection,
  onChange,
  loading = false,
  disabled = false,
}: CollectionItemsChecklistProps) {
  const summary = summarizeSelection(items, selection);

  const setQty = (item: RentalContractItem, qty: number) => {
    const clamped = Math.max(0, Math.min(Math.floor(qty) || 0, item.quantity));
    onChange({ ...selection, [item.movement_item_id]: clamped });
  };

  const toggle = (item: RentalContractItem, checked: boolean) => {
    onChange({ ...selection, [item.movement_item_id]: checked ? item.quantity : 0 });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          O que está sendo coletado
        </Label>
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          O que está sendo coletado
        </Label>
        <p className="text-sm text-muted-foreground">
          Nenhum item vinculado ao contrato foi encontrado. A coleta será registrada como total.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Package className="h-4 w-4" />
        O que está sendo coletado
      </Label>
      <div className="space-y-2">
        {items.map((item) => {
          const qty = selection[item.movement_item_id] ?? 0;
          const checked = qty > 0;
          const canAdjust = !item.is_unit && item.quantity > 1;
          return (
            <div
              key={item.movement_item_id}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                checked
                  ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/20'
                  : 'border-border opacity-70'
              }`}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={(c) => toggle(item, !!c)}
                aria-label={`Coletar ${item.name}`}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm leading-tight line-clamp-2">{item.name}</p>
                <p className="text-xs text-muted-foreground">
                  {item.serial_number ? `S/N ${item.serial_number} · ` : ''}
                  No contrato: {item.quantity}
                  {item.is_unit ? ' · por unidade, sai inteiro' : ''}
                </p>
              </div>
              {canAdjust ? (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    disabled={disabled || qty <= 0}
                    onClick={() => setQty(item, qty - 1)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={item.quantity}
                    value={qty}
                    disabled={disabled}
                    onChange={(e) => setQty(item, parseInt(e.target.value, 10))}
                    className="w-14 h-8 text-center"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    disabled={disabled || qty >= item.quantity}
                    onClick={() => setQty(item, qty + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Badge variant="outline" className="text-xs">
                  {checked ? `${qty}x` : 'fica'}
                </Badge>
              )}
            </div>
          );
        })}
      </div>

      {summary.isEmpty ? (
        <p className="text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Marque ao menos um item coletado.
        </p>
      ) : summary.isPartial ? (
        <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Coletando {summary.selectedQty} de {summary.totalQty}: coleta <strong>parcial</strong>. O contrato
            segue ativo com o que ficou, o valor é recalculado e a assinatura vigente será anulada.
          </span>
        </p>
      ) : (
        <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Coleta total: o contrato será encerrado.
        </p>
      )}
    </div>
  );
}
